import React, { useState } from "react";
import {
  Row,
  Col,
  Card,
  Typography,
  Divider,
  Select,
  Input,
  Button,
  message,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { fetchApplicationRequests, updateApplicationRequest } from "../../../Store/Slices/Admin/ApplicationRequests/applicationRequestsThunk";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Utility field display
const DetailField = ({ label, value }) => (
  <Row gutter={[16, 8]} className="mb-3">
    <Col span={8}>
      <Text strong className="text-gray-600">
        {label}
      </Text>
    </Col>
    <Col span={16}>
      <Text className="text-gray-800">{value || "N/A"}</Text>
    </Col>
  </Row>
);

const RequestDetails = ({ data, onClose }) => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const { userDetails } = useSelector((store) => store.common.user);
  const {
    statusFilter,
    applicantType,
    classId,
    sectionId,
    page,
    limit,
    searchText,
  } = useSelector((state) => state.admin.applicationRequests);

  const applicantName = `${data?.applicant?.firstName || ""} ${
    data?.applicant?.lastName || ""
  }`.trim();
  const submittedDate = moment(data?.createdAt).format("MMMM DD, YYYY");

  const [status, setStatus] = useState(data?.applicationStatus);
  const [internalComment, setInternalComment] = useState("");

  // refresh list after update
  const refreshRequests = () => {
    dispatch(
      fetchApplicationRequests({
        status: statusFilter,
        applicantType,
        applicationType:"benifit",
        classId,
        sectionId,
        page,
        limit,
        search: searchText,
      })
    );
  };

  const handleUpdate = async () => {
    if (!internalComment.trim()) {
      message.warning(t("Please add internal comment before updating."));
      return;
    }

    try {
      const res = await dispatch(
        updateApplicationRequest({
          applicationId: data._id,
          status,
          staffId: userDetails?.userId,
          comment: internalComment,
        })
      );

      if (res?.payload?.success) {
        message.success(res?.payload?.message || t("Request updated successfully"));
        refreshRequests();
        onClose?.();
      } else {
        message.error(
          res?.payload?.response?.data?.message || t("Failed to update request.")
        );
      }
    } catch (error) {
      message.error(t("Failed to update request."));
    }
  };

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
      <Row gutter={[24, 24]}>
        {/* LEFT COLUMN */}
        <Col xs={24} lg={14}>
          <Card
            className="shadow-sm rounded-lg"
            bordered={false}
            title={
              <div className="p-2">
                <Title level={4} className="mb-0 text-gray-800">
                  {t("Request Details")}
                </Title>
                <Text type="secondary" className="text-sm">
                  {t("Submitted by")} {applicantName} {t("on")} {submittedDate}
                </Text>
              </div>
            }
            bodyStyle={{ padding: "16px 24px" }}
          >
            <Title level={5} className="mt-0 mb-3 text-lg text-gray-800">
              {t("Request Information")}
            </Title>
            <DetailField label={t("Request Type")} value={data?.applicationType} />
            <DetailField label={t("Subject")} value={data?.subject} />

            <Row gutter={[16, 8]} className="mb-6">
              <Col span={8}>
                <Text strong className="text-gray-600">
                  {t("Description")}
                </Text>
              </Col>
              <Col span={16}>
                <div
                  className="prose max-w-none text-gray-800 text-sm"
                  dangerouslySetInnerHTML={{ __html: data?.description }}
                ></div>
              </Col>
            </Row>

            <DetailField label={t("Submitted On")} value={submittedDate} />

            <Divider className="my-6" />

            <Title level={5} className="mb-3 text-lg text-gray-800">
              {t("Employee Information")}
            </Title>
            <DetailField label={t("Employee Name")} value={applicantName} />
            <DetailField label={t("Department")} value={data?.department || "N/A"} />
            <DetailField label={t("Employee ID")} value={data?.employeeId || "-"} />
          </Card>
        </Col>

        {/* RIGHT COLUMN */}
        <Col xs={24} lg={10}>
          <Card className="shadow-sm rounded-lg" bordered={false}>
            <Title level={4} className="mt-0 mb-4 text-gray-800">
              {t("Manage Request")}
            </Title>

            <div className="mb-4">
              <Text strong className="text-gray-600 block mb-1">
                {t("Change Status")}
              </Text>
              <Select
                value={status}
                onChange={setStatus}
                placeholder={t("Select Status")}
                style={{ width: "100%" }}
              >
                <Option value="pending">{t("Pending")}</Option>
                <Option value="approved">{t("Approved")}</Option>
                <Option value="rejected">{t("Rejected")}</Option>
              </Select>
            </div>

            <div className="mb-4">
              <Text strong className="text-gray-600 block mb-1">
                {t("Notes")}
              </Text>
              <TextArea
                required
                rows={3}
                placeholder={t("Add internal comment (not visible to employee)")}
                value={internalComment}
                onChange={(e) => setInternalComment(e.target.value)}
                className="resize-none"
              />
            </div>

            <Button
              type="default"
              icon={<EditOutlined />}
              onClick={handleUpdate}
              block
              style={{
                color: "#ff8c00",
                borderColor: "#ff8c00",
                fontWeight: "600",
              }}
            >
              {t("Update Request")}
            </Button>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default RequestDetails;
